/**
 * Los dos momentos irreversibles: aplicar y revertir.
 *
 * APLICAR
 * -------
 * El dialogo no pregunta "¿estás seguro?". Preguntar eso no informa nada y se
 * contesta que si por reflejo. Repite los numeros —cuantos costos se reescriben,
 * cuantos productos nacen, cuanto sube el costo total por moneda— y muestra las
 * filas que mas se mueven, que son las que suelen delatar un error de lectura.
 *
 * Cuando hay algo grave (moneda que no coincide, variaciones enormes), pide una
 * marca explicita. No es burocracia: es la diferencia entre "apreté sin mirar" y
 * "lo miré y va igual", y esa diferencia es todo lo que separa a este flujo de
 * cargar los precios a mano.
 *
 * REVERTIR
 * --------
 * `revertir_lista_import()` deshace el lote entero devolviendo cada costo a su
 * valor previo, y el trigger recalcula los precios de vuelta. Es la red de
 * seguridad, y una red que hay que buscar en un menu no es una red: el boton
 * queda a la vista desde el momento en que el import se aplica.
 */
import { useEffect, useState } from "react";
import { Undo2 } from "lucide-react";

import { Importe } from "@/components/money";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { aNumero } from "@/lib/tipos";
import { formatearFechaHora } from "@/lib/utils";

import { AvisoMoneda } from "./AvisoMoneda";
import {
  formatearVariacion,
  resumenDeAplicado,
  resumenDeAplicar,
  variacionDe,
  type AlertaMoneda,
  type FilaRevision,
  type Impacto,
  type ImportLista,
  type ResultadoAplicar,
} from "./logica";

// ─────────────────────────────────────────────────────────────────────────────
// Aplicar
// ─────────────────────────────────────────────────────────────────────────────

export interface DialogAplicarProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  impacto: Impacto;
  alerta: AlertaMoneda | null;
  /** Las que mas se mueven, ya ordenadas. Se muestran las primeras. */
  sospechosas: ReadonlyArray<FilaRevision>;
  aplicando: boolean;
  onConfirmar: () => void;
}

const A_MOSTRAR = 5;

export function DialogAplicar({
  abierto,
  onAbiertoChange,
  impacto,
  alerta,
  sospechosas,
  aplicando,
  onConfirmar,
}: DialogAplicarProps): JSX.Element {
  const [mireLasRaras, setMireLasRaras] = useState(false);
  const [asumoLaMoneda, setAsumoLaMoneda] = useState(false);

  // Las marcas se resetean en cada apertura: una confirmacion heredada de la vez
  // anterior no confirma nada.
  useEffect(() => {
    if (abierto) {
      setMireLasRaras(false);
      setAsumoLaMoneda(false);
    }
  }, [abierto]);

  const pideRevisar = impacto.sospechosas > 0;
  const pideMoneda = alerta?.nivel === "GRAVE";
  const listo = (!pideRevisar || mireLasRaras) && (!pideMoneda || asumoLaMoneda);

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent tamano="md">
        <DialogHeader>
          <DialogTitle>Aplicar la lista</DialogTitle>
          <DialogDescription>{resumenDeAplicar(impacto)}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <AvisoMoneda alerta={alerta} />

          <Card variant="muted" padding="compacto">
            <dl className="grid grid-cols-2 gap-3">
              <Renglon titulo="Cambian de costo" valor={impacto.cambianCosto} />
              <Renglon titulo="Se dan de alta" valor={impacto.crean} />
            </dl>
          </Card>

          {impacto.porMoneda.map((m) => (
            <p key={m.moneda} className="text-sm">
              En {m.moneda}, el costo de{" "}
              {m.filas === 1 ? "ese producto pasa" : `esos ${m.filas} productos pasa`} de{" "}
              <Importe monto={m.costoAnterior} moneda={m.moneda} codigo={false} /> a{" "}
              <Importe monto={m.costoNuevo} moneda={m.moneda} codigo={false} />
              {m.pct === null ? "" : ` (${formatearVariacion(m.pct)})`}.
            </p>
          ))}

          {sospechosas.length > 0 ? (
            <div className="space-y-2">
              <TituloSeccion nivel="etiqueta">Las que más se mueven</TituloSeccion>
              <ul className="space-y-1">
                {sospechosas.slice(0, A_MOSTRAR).map((f) => {
                  const variacion = variacionDe(f);
                  return (
                    <li key={f.id} className="flex items-baseline justify-between gap-3 text-xs">
                      <span className="min-w-0 truncate">{f.descripcion}</span>
                      <span className="shrink-0 tabular-nums text-destructive">
                        {variacion === null
                          ? `${f.moneda_anterior} a ${f.moneda}`
                          : formatearVariacion(variacion)}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {sospechosas.length > A_MOSTRAR ? (
                <p className="text-xs text-muted-foreground">
                  y {sospechosas.length - A_MOSTRAR} más en la tabla.
                </p>
              ) : null}
            </div>
          ) : null}

          {pideRevisar ? (
            <Checkbox
              checked={mireLasRaras}
              onChange={(e) => setMireLasRaras(e.target.checked)}
              label={
                impacto.sospechosas === 1
                  ? "Miré la fila rara y va así."
                  : `Miré las ${impacto.sospechosas} filas raras y van así.`
              }
            />
          ) : null}
          {pideMoneda ? (
            <Checkbox
              checked={asumoLaMoneda}
              onChange={(e) => setAsumoLaMoneda(e.target.checked)}
              label="Entiendo lo de la moneda y quiero aplicar igual."
            />
          ) : null}

          <p className="text-xs text-muted-foreground">
            Todo esto queda bajo un mismo lote. Si algo sale mal, se revierte entero con un click.
          </p>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={aplicando}
            onClick={() => onAbiertoChange(false)}
          >
            Cancelar
          </Button>
          <Button type="button" disabled={!listo} cargando={aplicando} onClick={onConfirmar}>
            Sí, aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Renglon({ titulo, valor }: { titulo: string; valor: number }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="truncate text-xs text-muted-foreground">{titulo}</dt>
      <dd className="text-xl font-semibold tabular-nums">{valor}</dd>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Despues de aplicar
// ─────────────────────────────────────────────────────────────────────────────

export interface PanelAplicadoProps {
  imp: ImportLista;
  /** Lo que devolvio `aplicar_lista_import()` en esta sesion, si fue ahora. */
  resultado: ResultadoAplicar | null;
  /** El impacto de las filas aplicadas: cuantos costos cambiaron y cuantos no. */
  impacto: Impacto;
  revirtiendo: boolean;
  onRevertir: () => void;
}

export function PanelAplicado({
  imp,
  resultado,
  impacto,
  revirtiendo,
  onRevertir,
}: PanelAplicadoProps): JSX.Element {
  return (
    <Card tono="success" padding="normal" className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <TituloSeccion>La lista quedó aplicada</TituloSeccion>
          <p className="text-sm text-muted-foreground">
            {resultado
              ? resumenDeAplicado(impacto, resultado.creados)
              : `Se aplicaron ${imp.filas_total} filas de «${imp.archivo_nombre}».`}{" "}
            Los precios de venta ya se recalcularon.
          </p>
          {imp.aplicado_at ? (
            <p className="text-xs tabular-nums text-muted-foreground">
              {formatearFechaHora(imp.aplicado_at)}
              {imp.lote_id ? ` · lote ${imp.lote_id.slice(0, 8)}` : ""}
            </p>
          ) : null}
        </div>
        <Button type="button" variant="outline" onClick={onRevertir} cargando={revirtiendo}>
          <Undo2 aria-hidden="true" />
          Revertir todo
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Revertir devuelve cada costo a lo que valía antes y recalcula los precios. Se puede hacer
        cuando quieras: el lote queda guardado.
      </p>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Revertir
// ─────────────────────────────────────────────────────────────────────────────

export interface DialogRevertirProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  imp: ImportLista | null;
  revirtiendo: boolean;
  onConfirmar: () => void;
}

export function DialogRevertir({
  abierto,
  onAbiertoChange,
  imp,
  revirtiendo,
  onConfirmar,
}: DialogRevertirProps): JSX.Element {
  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent tamano="sm">
        <DialogHeader>
          <DialogTitle>Revertir la lista</DialogTitle>
          <DialogDescription>
            Cada producto que tocó este import vuelve al costo que tenía antes, y el precio de venta
            se recalcula con ese costo.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-2 text-sm">
          <p>
            Son las {imp === null ? "" : aNumero(imp.filas_total)} filas de «
            {imp?.archivo_nombre ?? "la lista"}».
          </p>
          {/*
            `revertir_lote_costos()` solo toca las lineas del historial que
            tienen costo anterior, y un alta no lo tiene. Decirlo evita que
            alguien revierta esperando que el producto nuevo desaparezca.
          */}
          <p className="text-muted-foreground">
            Los productos que se dieron de alta con esta lista no se borran ni se tocan: quedan en
            el catálogo con el costo que trajo la lista. Si no los querés, dalos de baja a mano.
          </p>
          <p className="text-muted-foreground">
            El historial no se reescribe: la reversión queda registrada como un lote nuevo.
          </p>
        </DialogBody>

        {/* El destructivo nombra la accion con su objeto (06 §2.4); "Sí" y "No"
            contestaban una pregunta y se aprietan por reflejo. */}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={revirtiendo}
            onClick={() => onAbiertoChange(false)}
          >
            Cancelar
          </Button>
          <Button type="button" variant="destructive" cargando={revirtiendo} onClick={onConfirmar}>
            Revertir la lista
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
